import { ReactElement, ReactNode } from "react";
import type { ImportResultSummary, PipelineRow } from "./resultSummaryModel";
import {
  PANEL,
  SECTION_TITLE,
  TABLE_CELL,
  TABLE_HEAD,
  TABLE_HEAD_RIGHT,
  TABLE_ROW,
} from "./staffSurface";

interface ImportResultSummaryProps {
  summary: ImportResultSummary;
}

export default function ImportResultSummaryTables({
  summary,
}: ImportResultSummaryProps): ReactElement {
  const { pipeline } = summary;

  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Pipeline</h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-sm text-gray-font">
          <thead>
            <tr>
              <th className={TABLE_HEAD}>&nbsp;</th>
              <th className={TABLE_HEAD_RIGHT}>Found</th>
              <th className={TABLE_HEAD_RIGHT}>Parsed</th>
              <th className={TABLE_HEAD_RIGHT}>Not parsed</th>
              <th className={TABLE_HEAD_RIGHT}>Added</th>
              <th className={TABLE_HEAD_RIGHT}>Updated</th>
              <th className={TABLE_HEAD_RIGHT}>Unchanged</th>
              <th className={TABLE_HEAD_RIGHT}>Deletable</th>
              <th className={TABLE_HEAD_RIGHT}>Write failed</th>
            </tr>
          </thead>
          <tbody>
            <PipelineTableRow label="Professionals" row={pipeline.professionals} />
            <PipelineTableRow
              label="Offices"
              row={pipeline.offices}
              hideFound
              hideRejected
              hideDeletable
            />
            <PipelineTableRow
              label="Practice Areas"
              row={pipeline.practiceAreas}
              hideFound
              hideRejected
              hideUpdated
              hideDeletable
            />
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Found is every professional the feed named. Parsed passed validation
        and is what the run will try to persist; Not parsed did not (a missing
        email, for example) and never reach the integrators. Offices and
        practice areas are taken from those already-parsed professionals, so
        their count sits under Parsed. Practice areas are only ever added — an
        existing one is never rewritten. Deletable are the firm&apos;s
        professionals no feed entry answered for; nothing is deleted.
      </p>
    </section>
  );
}

function PipelineTableRow({
  label,
  row,
  hideFound,
  hideRejected,
  hideUpdated,
  hideDeletable,
}: {
  label: string;
  row: PipelineRow;
  hideFound?: boolean;
  hideRejected?: boolean;
  hideUpdated?: boolean;
  hideDeletable?: boolean;
}): ReactElement {
  const parsedCount = hideFound ? row.parsed ?? row.found ?? 0 : row.parsed ?? 0;

  return (
    <tr className={TABLE_ROW}>
      <td className={TABLE_CELL}>{label}</td>
      <MetricCell muted={hideFound}>{hideFound ? "—" : row.found}</MetricCell>
      <MetricCell>{parsedCount}</MetricCell>
      <MetricCell muted={hideRejected}>
        {hideRejected ? "—" : row.rejected ?? 0}
      </MetricCell>
      <MetricCell>{row.added}</MetricCell>
      <MetricCell muted={hideUpdated}>{hideUpdated ? "—" : row.updated ?? 0}</MetricCell>
      <MetricCell>{row.unchanged}</MetricCell>
      <MetricCell muted={hideDeletable}>
        {hideDeletable ? "—" : row.deletable ?? 0}
      </MetricCell>
      <MetricCell>{row.writeFailed}</MetricCell>
    </tr>
  );
}

function MetricCell({
  children,
  muted,
}: {
  children: ReactNode;
  muted?: boolean;
}): ReactElement {
  return (
    <td
      className={`${TABLE_CELL} text-right tabular-nums ${muted ? "text-gray-400" : ""}`}
    >
      {children}
    </td>
  );
}
